import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../../../contexts/DataContext';
import { useAuth } from '../../../contexts/AuthContext';
import supabase from '../../../supabase/supabase';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import AutomaticProceduresPanel from './AutomaticProceduresPanel';

const {
  FiFileText, FiCheckCircle, FiTrash2, FiX, FiGlobe, FiEdit, FiSave,
  FiRefreshCw, FiPlus, FiUpload, FiInfo, FiLayout, FiUsers, FiBarChart2
} = FiIcons;

const TEMPLATE_CONTENT = {
  routine: `
# RUTINE FOR INTERN VARSLING

**1. Formål**
Formålet med denne rutinen er å oppfordre til åpenhet...`,
  ethics: `
# ETISKE RETNINGSLINJER

**1. Innledning**
Disse retningslinjene gjelder for alle ansatte...`,
  checklist: `
# SJEKKLISTE FOR SAKSBEHANDLING

**Mottak av varsel**
- [ ] Send bekreftelse til varsler (innen 7 dager)
- [ ] Vurder habilitet...`
};

const DEFAULT_DOCUMENTS = [
  { id: 1, title: 'Rutine for intern varsling', type: 'PDF', status: 'published', lastUpdated: new Date().toLocaleDateString(), required: true, description: 'Lovpålagt beskrivelse av fremgangsmåte ved varsling (AML § 2 A-6).', content: TEMPLATE_CONTENT.routine, publishedToPortal: true },
  { id: 2, title: 'Etiske retningslinjer', type: 'PDF', status: 'published', lastUpdated: new Date().toLocaleDateString(), required: false, description: 'Bedriftens interne regler for adferd og etikk.', content: TEMPLATE_CONTENT.ethics, publishedToPortal: true },
  { id: 3, title: 'Sjekkliste for saksbehandling', type: 'PDF', status: 'draft', lastUpdated: new Date().toLocaleDateString(), required: false, description: 'Internt hjelpedokument for HR/Ledelse ved mottak av varsel.', content: TEMPLATE_CONTENT.checklist, publishedToPortal: false }
];

const ProceduresManager = () => {
  const { user } = useAuth();
  const { companies, updateCompany, loading: dataLoading } = useData();

  // ROBUST SØK: Sjekk både companyId og company_id
  const targetId = user?.companyId || user?.company_id;
  const company = companies.find(c => c.id === targetId);

  const [documents, setDocuments] = useState([]);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [hasInitialized, setHasInitialized] = useState(false);
  const [showAutoProcedures, setShowAutoProcedures] = useState(false);
  const [ackStats, setAckStats] = useState({});

  useEffect(() => {
    if (company && !hasInitialized) {
      let storedDocs = company.documents;
      if (!storedDocs || storedDocs.length === 0) {
        storedDocs = company.raw_data?.documents;
      }
      if (!storedDocs || storedDocs.length === 0) {
        setDocuments(DEFAULT_DOCUMENTS);
      } else {
        setDocuments(storedDocs);
      }
      setHasInitialized(true);
      fetchAckStats();
    }
  }, [company, hasInitialized]);

  const fetchAckStats = async () => {
    if (!targetId) return;
    try {
      const { data, error } = await supabase
        .from('procedure_acknowledgements_1788620000000')
        .select('doc_id')
        .eq('company_id', targetId);
      
      if (data) {
        const stats = data.reduce((acc, curr) => {
          acc[curr.doc_id] = (acc[curr.doc_id] || 0) + 1;
          return acc;
        }, {});
        setAckStats(stats);
      }
    } catch (err) {
      console.error("Feil ved henting av statistikk:", err);
    }
  };

  const saveToDatabase = async (updatedDocs) => {
    if (!company) return;
    setIsSaving(true);
    try {
      await updateCompany(company.id, {
        documents: updatedDocs,
        raw_data: { ...(company.raw_data || {}), documents: updatedDocs }
      });
      setDocuments(updatedDocs);
    } catch (err) {
      console.error("Feil ved lagring av dokumenter:", err);
      alert("Kunne ikke lagre endringer. Prøv igjen.");
    } finally {
      setIsSaving(false);
    }
  };

  const togglePortalVisibility = (docId) => {
    const updated = documents.map(d =>
      d.id === docId ? { ...d, publishedToPortal: !d.publishedToPortal } : d
    );
    saveToDatabase(updated);
  };

  const toggleTracking = (docId) => {
    const updated = documents.map(d =>
      d.id === docId ? { ...d, trackAcknowledgement: !d.trackAcknowledgement } : d
    );
    saveToDatabase(updated);
  };

  const handleDelete = (id) => {
    if (window.confirm('Slette dokumentet permanent?')) {
      const updated = documents.filter(d => d.id !== id);
      saveToDatabase(updated);
    }
  };

  const startEditing = (doc) => {
    setViewingDoc(doc);
    setEditContent(doc.content || '');
    setIsEditing(true);
  };

  const saveEdit = () => {
    const updated = documents.map(d =>
      d.id === viewingDoc.id ? { ...d, content: editContent, lastUpdated: new Date().toLocaleDateString() } : d
    );
    saveToDatabase(updated);
    setIsEditing(false);
    setViewingDoc(null);
  };

  const handleCreateNew = () => {
    const newDoc = {
      id: Date.now(),
      title: 'Nytt dokument',
      description: 'Beskrivelse...',
      content: '# Ny overskrift',
      publishedToPortal: false,
      lastUpdated: new Date().toLocaleDateString(),
      type: 'PDF',
      status: 'draft'
    };
    const updated = [...documents, newDoc];
    saveToDatabase(updated);
    startEditing(newDoc);
  };

  const handleUpload = () => {
    // Mock upload functionality for now - creates a placeholder
    const newDoc = {
      id: Date.now(),
      title: 'Opplastet dokument.pdf',
      description: 'Lastet opp fra disk.',
      content: 'PDF content placeholder',
      publishedToPortal: false,
      lastUpdated: new Date().toLocaleDateString(),
      type: 'PDF',
      status: 'draft'
    };
    const updated = [...documents, newDoc];
    saveToDatabase(updated);
    alert("Dokument 'lastet opp' (simulert) og lagt til i listen.");
  };

  // --- RENDERING LOGIC ---
  if (dataLoading) return <div className="p-12 text-center"><SafeIcon icon={FiRefreshCw} className="animate-spin text-3xl text-gray-400 mx-auto mb-4" /><p className="text-gray-500">Henter data...</p></div>;
  if (!company) return <div className="p-8 bg-red-50 border border-red-200 rounded-xl text-red-700">Fant ikke bedriftsprofil.</div>;
  if (!hasInitialized) return <div className="p-12 text-center"><SafeIcon icon={FiRefreshCw} className="animate-spin text-3xl text-gray-400 mx-auto mb-4" /><p className="text-gray-500">Klargjør prosedyrer...</p></div>;

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            Prosedyrer og Rutiner
          </h2>
          <p className="text-sm text-gray-500">Administrer bedriftens styrende dokumenter og overvåk automatiske prosesser.</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => setShowAutoProcedures(false)}
            className={`${!showAutoProcedures ? 'bg-blue-600' : 'bg-gray-400'} text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 font-medium text-sm shadow-sm`}
          >
            Dokumentbibliotek
          </button>
          <button 
            onClick={() => setShowAutoProcedures(true)}
            className={`${showAutoProcedures ? 'bg-gray-800' : 'bg-gray-600'} text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2 font-medium text-sm shadow-sm`}
          >
            Automatiske Prosesser
          </button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {showAutoProcedures ? (
          <motion.div
            key="auto"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <AutomaticProceduresPanel />
          </motion.div>
        ) : (
          <motion.div
            key="docs"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-6"
          >
            {/* Info Alert Box - FIKSET FARGER FOR DAG/NATTMODUS */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-4 rounded-r-lg flex items-start gap-4">
              <SafeIcon icon={FiInfo} className="text-amber-600 dark:text-amber-500 text-xl mt-0.5" />
              <div>
                <h4 className="text-amber-800 dark:text-amber-500 font-bold text-sm mb-1">Viktig informasjon om maler</h4>
                <p className="text-amber-800 dark:text-amber-100/80 text-xs">
                  Dokumentene som ligger her som standard ("Rutine for intern varsling" osv.) er <strong>generelle maler</strong>. Husk å tilpasse dem før du publiserer til varslingssiden.
                </p>
              </div>
            </div>

            {/* Action Bar - FIKSET KNAPP FOR DAG/NATTMODUS */}
            <div className="flex justify-end">
              <button 
                onClick={handleUpload} 
                className="border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center gap-2 text-sm"
              >
                <SafeIcon icon={FiUpload} /> Last opp nytt dokument
              </button>
            </div>

            {/* Documents Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {documents.map((doc) => (
                <div key={doc.id} className="bg-white dark:bg-[#1e293b] rounded-xl border border-gray-200 dark:border-gray-700 p-5 flex flex-col h-full hover:border-blue-300 dark:hover:border-gray-600 transition-colors shadow-sm dark:shadow-none">
                  <div className="flex justify-between items-start mb-4">
                    <div className="p-2 bg-gray-100 dark:bg-gray-700/50 rounded-lg">
                      <SafeIcon icon={FiFileText} className="text-gray-500 dark:text-gray-400 text-xl" />
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${doc.publishedToPortal ? 'text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/30' : 'text-amber-700 bg-amber-100 dark:text-amber-400 dark:bg-amber-900/30'}`}>
                        {doc.publishedToPortal ? 'Publisert' : 'Utkast'}
                      </span>
                      {doc.trackAcknowledgement && (
                        <span className="text-[10px] px-2 py-0.5 rounded font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 flex items-center gap-1">
                          <SafeIcon icon={FiUsers} /> Sporing aktiv
                        </span>
                      )}
                    </div>
                  </div>
                  <h3 className="text-gray-900 dark:text-white font-bold text-sm mb-2">{doc.title}</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-xs mb-4 flex-grow line-clamp-3">{doc.description || 'Ingen beskrivelse'}</p>
                  
                  <div className="space-y-2 mb-4">
                    <button 
                      onClick={() => togglePortalVisibility(doc.id)} 
                      className={`w-full py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-2 transition-colors ${
                        doc.publishedToPortal 
                          ? 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800 dark:hover:bg-blue-900/50' 
                          : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600'
                      }`}
                    >
                      <SafeIcon icon={doc.publishedToPortal ? FiCheckCircle : FiGlobe} />
                      {doc.publishedToPortal ? 'Synlig på varslingsside' : 'Legg til på varslingsside'}
                    </button>

                    {doc.publishedToPortal && (
                      <button 
                        onClick={() => toggleTracking(doc.id)} 
                        className={`w-full py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-2 transition-colors ${
                          doc.trackAcknowledgement 
                            ? 'bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800' 
                            : 'bg-gray-50 text-gray-700 border border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600'
                        }`}
                      >
                        <SafeIcon icon={FiUsers} />
                        {doc.trackAcknowledgement ? 'Deaktiver sporing' : 'Aktiver lesebekreftelse'}
                      </button>
                    )}
                  </div>

                  {doc.trackAcknowledgement && (
                    <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg flex items-center justify-between border border-gray-100 dark:border-gray-700">
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <SafeIcon icon={FiBarChart2} />
                        <span>Bekreftede lesinger:</span>
                      </div>
                      <span className="font-black text-blue-600 dark:text-blue-400 text-sm">{ackStats[doc.id] || 0}</span>
                    </div>
                  )}

                  <div className="flex justify-between items-center pt-3 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-500">
                    <span>Oppdatert: {doc.lastUpdated}</span>
                    <div className="flex gap-2">
                      <button onClick={() => startEditing(doc)} className="hover:text-blue-600 dark:hover:text-blue-400"><SafeIcon icon={FiEdit} /></button>
                      <button onClick={() => handleDelete(doc.id)} className="hover:text-red-600 dark:hover:text-red-400"><SafeIcon icon={FiTrash2} /></button>
                    </div>
                  </div>
                </div>
              ))}

              {/* Add New Placeholder Card */}
              <button 
                onClick={handleCreateNew} 
                className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl p-5 flex flex-col items-center justify-center text-gray-500 hover:text-blue-600 dark:hover:text-gray-300 hover:border-blue-300 dark:hover:border-gray-500 hover:bg-blue-50 dark:hover:bg-gray-800/50 transition-colors min-h-[200px]"
              >
                <SafeIcon icon={FiPlus} className="text-3xl mb-2" />
                <span className="text-sm font-medium">Opprett fra mal</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Editor Modal (Same as before) */}
      <AnimatePresence>
        {isEditing && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white dark:bg-gray-900 w-full max-w-4xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] border border-gray-200 dark:border-gray-700">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-800">
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white text-xl">Rediger Dokument</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{viewingDoc?.title}</p>
                </div>
                <button onClick={() => setIsEditing(false)} className="p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white"><SafeIcon icon={FiX} className="text-xl" /></button>
              </div>
              <div className="flex-1 p-6 overflow-y-auto bg-white dark:bg-gray-900">
                <div className="mb-4">
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Tittel</label>
                  <input type="text" value={viewingDoc?.title || ''} onChange={(e) => setViewingDoc({ ...viewingDoc, title: e.target.value })} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-blue-500 focus:outline-none" />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Beskrivelse</label>
                  <input type="text" value={viewingDoc?.description || ''} onChange={(e) => setViewingDoc({ ...viewingDoc, description: e.target.value })} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-blue-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Innhold (Markdown)</label>
                  <textarea value={editContent} onChange={(e) => setEditContent(e.target.value)} className="w-full h-[400px] p-6 font-mono text-sm border border-gray-300 dark:border-gray-700 rounded-2xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-200 focus:border-blue-500 outline-none resize-none" placeholder="Skriv innholdet her..." />
                </div>
              </div>
              <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex justify-end gap-3">
                <button onClick={() => setIsEditing(false)} className="px-6 py-3 text-gray-600 dark:text-gray-300 font-bold hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl transition-colors">Avbryt</button>
                <button onClick={saveEdit} className="px-8 py-3 bg-blue-600 text-white rounded-xl font-bold shadow-lg flex items-center gap-2 hover:bg-blue-700 transition-colors">
                  <SafeIcon icon={FiSave} /> Lagre
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProceduresManager;